import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TAGS = ["all", "silver", "gold", "diamond", "ring", "earring", "coin"];

export default function AdminFaqs() {
  const [faqs, setFaqs] = useState([]);
  const [draft, setDraft] = useState({ question: "", answer: "", tags: [] });

  const load = () => api.get("/admin/faqs").then((r) => setFaqs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const seed = async () => {
    try {
      const r = await api.post("/admin/faqs/seed");
      toast.success(`Seeded ${r.data.inserted} FAQs (${r.data.flagged} flagged for policy confirmation)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (f) => {
    try {
      await api.put(`/admin/faqs/${f.id || f._id}`, f);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const add = async () => {
    if (!draft.question.trim()) return toast.error("Question required");
    try {
      await api.post("/admin/faqs", draft);
      setDraft({ question: "", answer: "", tags: [] });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleTag = (obj, setObj, t) =>
    setObj({ ...obj, tags: obj.tags.includes(t) ? obj.tags.filter((x) => x !== t) : [...obj.tags, t] });

  return (
    <div className="max-w-4xl space-y-5" data-testid="admin-faqs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Product FAQ Library</h1>
          <p className="text-sm text-neutral-500 mt-1">
            FAQs auto-assign to products by tags (metal / category / type) and render as an accordion with FAQPage structured data.
            Entries with a blank answer are flagged and never shown on the storefront.
          </p>
        </div>
        {!faqs.length && <Button onClick={seed} className="bg-stone-900 text-white" data-testid="faq-seed-btn">Seed starter library</Button>}
      </div>

      <section className="bg-white border border-neutral-200 p-5 space-y-3" data-testid="faq-add">
        <h2 className="font-heading font-semibold">Add FAQ</h2>
        <Input placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} data-testid="faq-new-q" />
        <Textarea rows={2} placeholder="Answer — short, plain, no marketing filler. Leave blank to flag for client confirmation." value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} data-testid="faq-new-a" />
        <div className="flex flex-wrap gap-2">
          {TAGS.map((t) => (
            <label key={t} className="text-xs flex items-center gap-1 border border-neutral-200 px-2 py-1">
              <input type="checkbox" checked={draft.tags.includes(t)} onChange={() => toggleTag(draft, setDraft, t)} data-testid={`faq-new-tag-${t}`} /> {t}
            </label>
          ))}
        </div>
        <Button size="sm" onClick={add} data-testid="faq-add-btn"><Plus size={14} className="mr-1" /> Add</Button>
      </section>

      {faqs.map((f) => (
        <section key={f.id || f._id} className={`bg-white border p-5 space-y-2 ${f.needs_confirmation ? "border-amber-300" : "border-neutral-200"}`} data-testid={`faq-${f.id || f._id}`}>
          {f.needs_confirmation && <p className="text-[10px] font-mono uppercase text-amber-700 bg-amber-50 border border-amber-200 inline-block px-1.5 py-0.5">Needs client confirmation — hidden from storefront</p>}
          <Input value={f.question} onChange={(e) => setFaqs(faqs.map((x) => (x === f ? { ...x, question: e.target.value } : x)))} data-testid={`faq-q-${f.id || f._id}`} />
          <Textarea rows={2} value={f.answer} onChange={(e) => setFaqs(faqs.map((x) => (x === f ? { ...x, answer: e.target.value } : x)))} data-testid={`faq-a-${f.id || f._id}`} />
          <div className="flex flex-wrap items-center gap-2">
            {TAGS.map((t) => (
              <label key={t} className="text-xs flex items-center gap-1 border border-neutral-200 px-2 py-1">
                <input type="checkbox" checked={(f.tags || []).includes(t)}
                  onChange={() => setFaqs(faqs.map((x) => (x === f ? { ...x, tags: x.tags.includes(t) ? x.tags.filter((y) => y !== t) : [...x.tags, t] } : x)))} /> {t}
              </label>
            ))}
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => save(f)} data-testid={`faq-save-${f.id || f._id}`}>Save</Button>
              <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/faqs/${f.id || f._id}`); load(); }} data-testid={`faq-del-${f.id || f._id}`}><Trash2 size={14} /></Button>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
